/**
 * Store central — état partagé observable, unique source de vérité.
 *
 * Vanilla, sans dépendance : `setState` fusionne et notifie les abonnés dont
 * la tranche sélectionnée a changé (comparaison par référence). Remplace la
 * dispersion d'état de l'ancienne architecture (état d'optimisation, modèle
 * sélectionné, cible d'exécution recopiés dans plusieurs modules).
 */
import type {
	AuthUser,
	EmbeddingRef,
	LoraRef,
	ModelInfo,
	OptimizationOptions,
	StatusResponse,
	VaeRef,
	WorkerCapabilities,
} from "../types/index.js";

export type ExecutionTarget = { kind: "local" } | { kind: "hosted"; worker: WorkerCapabilities };

export interface CurrentResult {
	kind: "image" | "video" | "audio";
	url: string;
	filename: string;
	seed?: number;
	generationTime?: number;
	modelId?: string;
	images?: string[];
	warning?: string | null;
	error?: string | null;
	/** The failed job, so « Envoyer le rapport » can say which one. */
	jobId?: string | null;
	enhancedPrompt?: string | null;
}

/** A LoRA the person ticked, with what the page knows of it. */
export interface LoraSelection extends LoraRef {
	name: string;
	/** Empty when unknown (typed in, not yet on the machine). */
	architectures: string[];
}

/** An embedding the person ticked; `token` is what the prompt must say. */
export interface EmbeddingSelection extends EmbeddingRef {
	name: string;
	token: string;
	architectures: string[];
	vectors?: number | null;
}

export interface VaeSelection extends VaeRef {
	name: string;
	architectures: string[];
}

/**
 * Ce qui tourne en ce moment, où que ce soit. `null` = rien en cours.
 *
 * Une seule tranche pour les deux routes d'exécution, parce qu'il n'y a
 * jamais qu'une génération à la fois et qu'un écran ne peut en montrer qu'une.
 * Avant, la page tenait trois comptes séparés de la même chose : un booléen
 * `generating` qu'aucun abonné n'écoutait, ce bloc-ci pour la machine pairée,
 * et `status.generation_progress` pour la machine locale — dont chacun ne
 * connaissait qu'une moitié du monde. Le bouton, la barre du haut et le
 * panneau de résultat lisent maintenant la même chose.
 */
export interface RunProgress {
	/** Phrase à afficher (« Préparation de sd15… », « Génération… »). */
	message: string;
	/** 0-100, ou null quand rien n'a encore été chiffré. */
	percent: number | null;
	/** La machine pairée n'est plus vue en ligne alors que le job tourne. */
	machineGone: boolean;
}

export interface AppState {
	status: StatusResponse | null;
	selectedModel: ModelInfo | null;
	/** LoRAs and VAE for the next image, carried in its request. */
	loras: LoraSelection[];
	vae: VaeSelection | null;
	embeddings: EmbeddingSelection[];
	optimizationOptions: OptimizationOptions | null;
	executionTarget: ExecutionTarget;
	/** Utilisateur connecté (null = anonyme). */
	user: AuthUser | null;
	/** Machines de l'utilisateur, rapportées par le workspace Compte. */
	workers: WorkerCapabilities[];
	currentResult: CurrentResult | null;
	/** Ce qui tourne en ce moment, ici ou sur la machine pairée. */
	run: RunProgress | null;
	/** Incrémenté après chaque génération : le panneau droit rafraîchit l'historique. */
	historyVersion: number;
}

const initialState: AppState = {
	status: null,
	selectedModel: null,
	loras: [],
	vae: null,
	embeddings: [],
	optimizationOptions: null,
	executionTarget: { kind: "local" },
	user: null,
	workers: [],
	currentResult: null,
	run: null,
	historyVersion: 0,
};

type Listener<T> = (value: T, prev: T) => void;

interface Subscription {
	selector: (s: AppState) => unknown;
	listener: Listener<unknown>;
}

let state: AppState = initialState;
// One entry per subscribe() call, not per listener function. The first version
// keyed a Map by the listener, so subscribing the same function to two slices
// kept only the last: the execution picker, subscribed with one `render` to
// workers, user and status, heard about none but status -- and the hosted page
// never offered the user's own machine.
const subscriptions = new Set<Subscription>();

export const store = {
	get(): AppState {
		return state;
	},

	setState(patch: Partial<AppState>): void {
		const prev = state;
		state = { ...state, ...patch };
		// A snapshot: a listener that subscribes or unsubscribes while being
		// notified must not change who hears about *this* change.
		for (const { selector, listener } of [...subscriptions]) {
			const next = selector(state);
			const before = selector(prev);
			if (next !== before) listener(next, before);
		}
	},

	/**
	 * Abonne listener à une tranche de l'état. La tranche est comparée par
	 * référence : retourner un objet recalculé à chaque appel notifie sans
	 * arrêt — sélectionner une référence stable (champ d'état ou valeur primitive).
	 */
	subscribe<T>(selector: (s: AppState) => T, listener: Listener<T>): () => void {
		const entry: Subscription = { selector, listener: listener as Listener<unknown> };
		subscriptions.add(entry);
		return () => {
			subscriptions.delete(entry);
		};
	},
};
